import { motion } from "motion/react";
import Header from "../../../components/feature/Header";
import Footer from "../../../components/feature/Footer";
import WhatsAppFloating from "../../../components/feature/WhatsAppFloating";
import CookieBanner from "../../../components/feature/CookieBanner";
import QuoteFormSection from "../../home/components/QuoteFormSection";
import SEO from "@/components/feature/SEO";

const porQue = [
  { stat: "300+", label: "días de sol al año", desc: "Málaga tiene uno de los climas más soleados de Europa. Tu balcón necesita protección." },
  { stat: "50°C", label: "temperatura en balcón", desc: "Sin toldo, la temperatura en tu balcón puede superar los 50°C en pleno verano. Imposible usarlo." },
  { stat: "100%", label: "más usable", desc: "Con un toldo de calidad tu balcón pasa de un espacio abandonado a tu rincón favorito de la casa." },
];

const tipos = [
  {
    icon: "ri-archive-2-line",
    title: "Toldo cofre balcón",
    desc: "El más recomendado. La lona y los brazos se protegen dentro de una caja de aluminio cuando está cerrado. Mayor durabilidad y estética limpia.",
    tag: "Recomendado",
    tagColor: "bg-green-100 text-green-700",
  },
  {
    icon: "ri-square-line",
    title: "Toldo punto recto",
    desc: "El clásico toldo de brazos extensibles. Más económico, funcional y disponible en muchos colores. Perfecto para balcones estándar.",
    tag: "Económico",
    tagColor: "bg-secondary/10 text-secondary",
  },
  {
    icon: "ri-arrow-up-down-line",
    title: "Toldo capota",
    desc: "Toldo vertical con forma de capota. Protege del sol de frente y del viento. Ideal para balcones en altura o con mucha exposición.",
    tag: "Antiviento",
    tagColor: "bg-accent/20 text-amber-700",
  },
];

const permisos = [
  {
    icon: "ri-file-paper-line",
    title: "Comunidad de vecinos",
    desc: "Generalmente requiere aprobación en junta de vecinos para mantener la estética uniforme del edificio (color, tamaño, tipo). Te asesoramos en el trámite.",
  },
  {
    icon: "ri-government-line",
    title: "Ayuntamiento de Málaga",
    desc: "Para toldos de balcón no suele requerirse licencia mayor. Una declaración responsable o comunicación previa es suficiente en la mayoría de casos.",
  },
];

const precios = [
  { tipo: "Punto recto 2x1,5m", precio: "350 - 500 €", emoji: "🏠" },
  { tipo: "Cofre balcón 2,5x2m", precio: "500 - 750 €", emoji: "📦" },
  { tipo: "Capota antiviento", precio: "400 - 600 €", emoji: "🛡️" },
  { tipo: "Cofre motorizado balcón", precio: "650 - 900 €", emoji: "⚡" },
];

const faqs = [
  {
    q: "¿Necesito permiso del ayuntamiento?",
    a: "En la mayoría de casos no. Un toldo de balcón se considera obra menor que no requiere licencia. Solo necesitas presentar una declaración responsable o comunicación previa al Ayuntamiento de Málaga. Si el toldo excede ciertas dimensiones o sobresale mucho de la fachada, puede requerirse informe técnico. Te asesoramos gratis.",
  },
  {
    q: "¿Y de la comunidad de vecinos?",
    a: "Sí, generalmente sí. La Ley de Propiedad Horizontal establece que elementos exteriores visibles deben respetar la estética del edificio. Normalmente se aprueba en junta si el toldo es del color y tipo acordado por la comunidad. Te ayudamos a preparar la documentación y el informe técnico si lo necesitas.",
  },
  {
    q: "¿Qué pasa si vivo en un piso alto?",
    a: "No hay problema. Instalamos toldos en plantas altas habitualmente. Contamos con técnicos con experiencia en trabajos verticales y plataformas si es necesario. El viento es mayor en altura, por eso recomendamos cofre o capota antiviento. El sensor de viento del motorizado recoge automáticamente ante ráfagas.",
  },
  {
    q: "¿Resiste el viento fuerte?",
    a: "Los toldos cofre y capota resisten mucho mejor el viento que los punto recto estándar. El cofre protege la lona cuando está cerrado. Con motorización Somfy, el sensor de viento recoge automáticamente el toldo cuando detecta ráfagas superiores al umbral configurado (normalmente 40-50 km/h).",
  },
  {
    q: "¿Cuánto dura un toldo en balcón Málaga?",
    a: "Un toldo de calidad bien mantenido dura entre 8 y 12 años en Málaga. El sol intenso es el principal desgaste. Por eso recomendamos lonas Tempotest o Sauleda (anti-UV) y toldos cofre (la lona está protegida cuando no se usa). Con mantenimiento anual mínimo (limpieza, lubricación) llegan fácilmente a 15 años.",
  },
];

export default function ToldosBalcón() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Toldos Balcón Málaga | Compactos desde 350€"
        description="Toldos para balcón en Málaga desde 350€. Cofre, recto, capota. Discretos y eficientes. Garantía."
        canonical="/toldos/toldos-balcon/"
        breadcrumbs={[{ name: "Inicio", path: "/" }, { name: "Toldos", path: "/toldos" }, { name: "Toldos balcón" }]}
      />
      <Header />
      <main>
        {/* HERO */}
        <section className="bg-white pt-24 md:pt-[72px]">
          <div className="max-w-container mx-auto px-4 md:px-6 py-12 md:py-24">
            <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
              <div className="w-full lg:w-[60%] flex flex-col gap-6">
                <div className="inline-flex items-center gap-2 bg-accent text-text-primary font-body font-semibold text-sm px-4 py-2 rounded-pill w-fit">
                  <span>⚡</span> Desde 350€
                </div>
                <h1 className="font-heading font-extrabold text-4xl md:text-5xl lg:text-[56px] leading-[1.1] text-text-primary">
                  Toldos para Balcón en Málaga
                </h1>
                <p className="font-body text-lg md:text-xl text-text-secondary leading-relaxed max-w-xl">
                  Disfruta de tu balcón sin que el sol te queme. Toldos compactos y discretos desde 350€.
                </p>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <a
                    href="tel:+34951120770"
                    className="inline-flex items-center gap-2 bg-primary hover:bg-primary-dark text-white font-body font-bold text-lg md:text-xl px-8 py-4 rounded-pill transition-colors whitespace-nowrap"
                  >
                    <span>📞</span> 951 12 07 70
                  </a>
                  <a
                    href="#presupuesto"
                    className="inline-flex items-center gap-2 border-2 border-secondary text-secondary hover:bg-secondary hover:text-white font-body font-semibold text-base px-6 py-3.5 rounded-pill transition-colors whitespace-nowrap"
                  >
                    Pedir presupuesto
                  </a>
                </div>
                <div className="flex flex-wrap items-center gap-4 md:gap-6 mt-2">
                  <span className="flex items-center gap-1.5 font-body font-medium text-sm text-text-secondary"><span className="text-green-600">✓</span> +10 años · desde 2015</span>
                  <span className="flex items-center gap-1.5 font-body font-medium text-sm text-text-secondary"><span className="text-green-600">✓</span> Desde 350€</span>
                  <span className="flex items-center gap-1.5 font-body font-medium text-sm text-text-secondary"><span className="text-green-600">✓</span> Garantía 5 años motor</span>
                </div>
              </div>
              <div className="w-full lg:w-[40%] relative">
                <div className="relative rounded-3xl overflow-hidden lg:-rotate-1 shadow-xl">
                  <div className="absolute inset-0 bg-blue-900/35 z-10 pointer-events-none"></div>
                  <img
                    src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&q=80&auto=format&fit=crop"
                    alt="Toldo para balcón en Málaga - compacto y elegante"
                    className="w-full h-auto object-cover aspect-[4/3]"
                    loading="eager"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ¿POR QUÉ UN TOLDO EN TU BALCÓN? */}
        <section className="bg-surface-alt py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">¿Por qué un toldo en tu balcón en Málaga?</h2>
              <div className="w-10 h-1 bg-primary mx-auto rounded-full"></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {porQue.map((p, i) => (
                <motion.div
                  key={p.label}
                  className="bg-white rounded-[20px] p-8 text-center shadow-sm hover:shadow-md transition-shadow border-t-[6px] border-primary"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  <div className="font-heading font-extrabold text-4xl md:text-5xl text-primary mb-2">{p.stat}</div>
                  <div className="font-heading font-bold text-lg text-text-primary mb-2">{p.label}</div>
                  <p className="font-body text-sm text-text-secondary leading-relaxed">{p.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* TIPOS PARA BALCÓN */}
        <section className="bg-white py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">Tipos para balcón</h2>
              <div className="w-10 h-1 bg-primary mx-auto rounded-full"></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {tipos.map((t, i) => (
                <motion.div
                  key={t.title}
                  className="bg-surface-alt rounded-[20px] p-8 shadow-sm hover:shadow-md transition-shadow relative"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  <span className={`inline-block font-body font-semibold text-xs px-3 py-1 rounded-full mb-3 ${t.tagColor}`}>
                    {t.tag}
                  </span>
                  <div className="w-12 h-12 bg-primary/10 text-primary rounded-xl flex items-center justify-center mb-5">
                    <i className={`${t.icon} text-2xl`}></i>
                  </div>
                  <h3 className="font-heading font-bold text-xl text-text-primary mb-2">{t.title}</h3>
                  <p className="font-body text-sm text-text-secondary leading-relaxed">{t.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* PERMISOS */}
        <section className="bg-surface-alt py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">Normativa y permisos</h2>
              <div className="w-10 h-1 bg-primary mx-auto rounded-full"></div>
              <p className="font-body text-lg text-text-secondary max-w-2xl mx-auto mt-4">
                Te ayudamos con toda la documentación. No te preocupes por los trámites.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {permisos.map((p, i) => (
                <motion.div
                  key={p.title}
                  className="bg-white rounded-[20px] p-8 shadow-sm hover:shadow-md transition-shadow"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  <div className="w-12 h-12 bg-primary/10 text-primary rounded-xl flex items-center justify-center mb-5">
                    <i className={`${p.icon} text-2xl`}></i>
                  </div>
                  <h3 className="font-heading font-bold text-xl text-text-primary mb-2">{p.title}</h3>
                  <p className="font-body text-sm text-text-secondary leading-relaxed">{p.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* PRECIOS */}
        <section className="bg-white py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">Precios orientativos para balcón</h2>
              <div className="w-10 h-1 bg-primary mx-auto rounded-full"></div>
              <p className="font-body text-lg text-text-secondary mt-4 max-w-2xl mx-auto">
                Todos los precios incluyen material, instalación completa y garantía. Presupuesto exacto tras visita técnica.
              </p>
            </div>
            <div className="max-w-2xl mx-auto bg-surface-alt rounded-3xl overflow-hidden">
              {precios.map((p, i) => (
                <div
                  key={p.tipo}
                  className={`flex items-center justify-between px-6 md:px-8 py-5 ${i !== precios.length - 1 ? "border-b border-border" : ""}`}
                >
                  <span className="flex items-center gap-3 font-body text-base md:text-lg text-text-primary">
                    <span>{p.emoji}</span> {p.tipo}
                  </span>
                  <span className="font-heading font-bold text-lg md:text-xl text-primary">{p.precio}</span>
                </div>
              ))}
            </div>
            <p className="text-center font-body text-sm text-text-secondary mt-6">
              Precios orientativos según dimensiones exactas del balcón, tipo de toldo y acabados elegidos.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-primary py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">Disfruta tu balcón todo el año</h2>
            <p className="font-body text-lg text-white/90 max-w-xl mx-auto mb-8">
              Te visitamos gratis, medimos tu balcón y te damos presupuesto en 24h. Todos los trámites incluidos.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="tel:+34951120770"
                className="inline-flex items-center gap-2 bg-white hover:bg-slate-100 text-primary font-body font-bold text-lg px-8 py-4 rounded-pill transition-colors whitespace-nowrap"
              >
                <span>📞</span> 951 12 07 70
              </a>
              <a
                href="#presupuesto"
                className="inline-flex items-center gap-2 border-2 border-white text-white hover:bg-white hover:text-primary font-body font-semibold text-base px-6 py-3.5 rounded-pill transition-colors whitespace-nowrap"
              >
                Pedir presupuesto online
              </a>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-white py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto" itemScope itemType="https://schema.org/FAQPage">
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary text-center mb-12">Preguntas frecuentes sobre toldos de balcón</h2>
              <div className="flex flex-col gap-4">
                {faqs.map((f, i) => (
                  <motion.div
                    key={i}
                    className="bg-surface-alt rounded-2xl p-6"
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.08 }}
                    itemScope
                    itemProp="mainEntity"
                    itemType="https://schema.org/Question"
                  >
                    <h3 className="font-heading font-bold text-lg text-text-primary mb-2" itemProp="name">{f.q}</h3>
                    <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                      <p className="font-body text-sm text-text-secondary leading-relaxed" itemProp="text">{f.a}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FORM */}
        <div id="presupuesto">
          <QuoteFormSection />
        </div>
      </main>
      <Footer />
      <WhatsAppFloating />
      <CookieBanner />
    </div>
  );
}